import React, { useEffect, useState } from "react";
import {
  Box, Typography, IconButton, Chip, Avatar,
  CircularProgress, Collapse, Button, TextField, Dialog,
  DialogContent, DialogActions,
} from "@mui/material";
import CenteredDetailModal from "./CenteredDetailModal";
import CloseIcon            from "@mui/icons-material/Close";
import PersonIcon           from "@mui/icons-material/Person";
import EmailIcon            from "@mui/icons-material/Email";
import PhoneIcon            from "@mui/icons-material/Phone";
import CalendarTodayIcon    from "@mui/icons-material/CalendarToday";
import GoogleIcon           from "@mui/icons-material/Google";
import LockIcon             from "@mui/icons-material/Lock";
import PaymentIcon          from "@mui/icons-material/Payment";
import AccessTimeIcon       from "@mui/icons-material/AccessTime";
import QuizIcon             from "@mui/icons-material/Quiz";
import CardGiftcardIcon     from "@mui/icons-material/CardGiftcard";
import LocationOnIcon       from "@mui/icons-material/LocationOn";
import CreditCardIcon       from "@mui/icons-material/CreditCard";
import ReceiptIcon          from "@mui/icons-material/Receipt";
import CancelIcon           from "@mui/icons-material/Cancel";
import ExpandMoreIcon       from "@mui/icons-material/ExpandMore";
import ExpandLessIcon       from "@mui/icons-material/ExpandLess";
import SwapHorizIcon        from "@mui/icons-material/SwapHoriz";
import ShieldIcon           from "@mui/icons-material/Shield";
import WarningAmberIcon     from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PauseCircleIcon      from "@mui/icons-material/PauseCircle";
import PlayCircleIcon       from "@mui/icons-material/PlayCircle";
import TokenIcon            from "@mui/icons-material/Token";
import moment from "moment";
import { useSnackbar } from "notistack";
import { baseUrl } from "../config/config";
import {
  get_user_stripe_details_api,
  admin_grant_trial_api,
  admin_change_plan_api,
  admin_cancel_subscription_api,
  admin_get_user_insurance_api,
  admin_activate_insurance_api,
  admin_cancel_insurance_api,
} from "../DAL/Stripe/Stripe";
import {
  admin_get_user_tokens_api,
  admin_adjust_tokens_api,
  admin_charge_credits_api,
  admin_resume_pause_api,
  admin_get_user_lessons_api,
} from "../DAL/Credits/Credits";

// ── Sub-components ────────────────────────────────────────────────────────────

const InfoRow = ({ icon, label, value, chip, chipColor, mono }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, py: 1.25, borderBottom: "1px solid rgba(26,26,26,0.06)" }}>
    <Box sx={{ color: "#c01e2e", mt: 0.5, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 0.4, fontFamily: "'Rubik', sans-serif", textTransform: "uppercase", letterSpacing: "0.3px" }}>
        {label}
      </Typography>
      {chip ? (
        <Chip label={value} size="small" color={chipColor || "default"} sx={{ borderRadius: "6px", fontWeight: 500, fontSize: "12px" }} />
      ) : (
        <Typography sx={{ color: "#1a1a1a", fontSize: mono ? "12px" : "13px", wordBreak: "break-all", fontFamily: mono ? "monospace" : "'Rubik', sans-serif" }}>
          {value || "—"}
        </Typography>
      )}
    </Box>
  </Box>
);

const Section = ({ title, children, collapsible, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: collapsible ? "pointer" : "default", mb: 1, py: 0.5 }}
        onClick={() => collapsible && setOpen(!open)}>
        <Typography sx={{ color: "#c01e2e", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: "'Rubik', sans-serif" }}>
          {title}
        </Typography>
        {collapsible && (open
          ? <ExpandLessIcon sx={{ color: "#aaa", fontSize: 18 }} />
          : <ExpandMoreIcon sx={{ color: "#aaa", fontSize: 18 }} />
        )}
      </Box>
      <Collapse in={!collapsible || open}>
        <Box sx={{ backgroundColor: "#F7F4EF", borderRadius: "10px", p: "4px 12px 4px", border: "1px solid rgba(26,26,26,0.07)" }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatCurrency = (amount, currency = "gbp") => {
  if (amount === null || amount === undefined) return "—";
  const symbols = { gbp: "£", usd: "$", eur: "€" };
  return `${symbols[currency.toLowerCase()] || ""}${(amount / 100).toFixed(2)}`;
};

const formatStripeDate = (ts) => ts ? moment.unix(ts).format("DD MMM YYYY, HH:mm") : "—";

const dialogInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "9px", color: "#1a1a1a", backgroundColor: "#F7F4EF",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { color: "#6b6b6b" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

// ── Main component ────────────────────────────────────────────────────────────

export default function UserDetailDrawer({ open, onClose, user }) {
  const { enqueueSnackbar } = useSnackbar();

  // ── Stripe ──────────────────────────────────────────────────────────────────
  const [stripeData,    setStripeData]    = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError,   setStripeError]   = useState(null);

  const fetchStripeData = async () => {
    setStripeLoading(true); setStripeError(null);
    try {
      const r = await get_user_stripe_details_api(user.stripeCustomerId);
      if (r.code === 200) setStripeData(r.data);
      else setStripeError(r.message || "Failed to load Stripe data");
    } catch { setStripeError("Failed to load Stripe data"); }
    finally { setStripeLoading(false); }
  };

  // ── Credits / tokens ────────────────────────────────────────────────────────
  const [tokenData,    setTokenData]    = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  const fetchTokenData = async () => {
    setTokenLoading(true);
    try {
      const r = await admin_get_user_tokens_api(user._id);
      if (r.code === 200) setTokenData(r.data);
    } catch { /* silently ignore */ }
    finally { setTokenLoading(false); }
  };

  // ── Lessons ──────────────────────────────────────────────────────────────────
  const [lessonData,    setLessonData]    = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);

  const fetchLessonData = async () => {
    setLessonLoading(true);
    try {
      const r = await admin_get_user_lessons_api(user._id);
      if (r.code === 200) setLessonData(r.data);
    } catch { /* silently ignore */ }
    finally { setLessonLoading(false); }
  };

  // ── Insurance ───────────────────────────────────────────────────────────────
  const [insuranceData,          setInsuranceData]          = useState(null);
  const [insuranceLoading,       setInsuranceLoading]       = useState(false);
  const [insuranceActionLoading, setInsuranceActionLoading] = useState(false);
  const [insuranceCancelOpen,    setInsuranceCancelOpen]    = useState(false);

  const fetchInsuranceData = async () => {
    setInsuranceLoading(true);
    try {
      const r = await admin_get_user_insurance_api(user._id);
      if (r.code === 200) setInsuranceData(r);
    } catch { /* ignore */ }
    finally { setInsuranceLoading(false); }
  };

  useEffect(() => {
    if (open && user?._id) {
      if (user.stripeCustomerId) fetchStripeData();
      fetchTokenData();
      fetchLessonData();
      fetchInsuranceData();
    } else {
      setStripeData(null); setStripeError(null);
      setTokenData(null);
      setLessonData(null);
      setInsuranceData(null);
    }
  }, [open, user]);

  // ── Dialog states ────────────────────────────────────────────────────────────
  const [trialDialogOpen,  setTrialDialogOpen]  = useState(false);
  const [planDialogOpen,   setPlanDialogOpen]   = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [trialDays,        setTrialDays]        = useState(7);
  const [trialPriceId,     setTrialPriceId]     = useState("");
  const [selectedPriceId,  setSelectedPriceId]  = useState("");
  const [cancelImmediate,  setCancelImmediate]  = useState(false);
  const [actionLoading,    setActionLoading]    = useState(false);

  // Credit dialogs
  const [addCreditsOpen,    setAddCreditsOpen]    = useState(false);
  const [deductCreditsOpen, setDeductCreditsOpen] = useState(false);
  const [chargeCardOpen,    setChargeCardOpen]    = useState(false);
  const [creditAmount,      setCreditAmount]      = useState("");
  const [creditReason,      setCreditReason]      = useState("");
  const [creditLoading,     setCreditLoading]     = useState(false);

  // Pause dialog
  const [resumePauseOpen,  setResumePauseOpen]  = useState(false);
  const [pauseLoading,     setPauseLoading]     = useState(false);

  const PLAN_OPTIONS = [
    { priceId: "price_1ReahYKDAoGYDAZ5JWzFYfTJ", name: "Basic", label: "Basic" },
    { priceId: "price_1QXA2HKDAoGYDAZ53VVuJyoE", name: "PAYG", label: "PAYG" },
    { priceId: "price_1Qf0MHKDAoGYDAZ5YRX2uL4d", name: "Lite", label: "Lite" },
    { priceId: "price_1TfeIHKDAoGYDAZ5sj2KGcD1", name: "Standard", label: "Standard (£299/6mo)" },
    { priceId: "price_1TfeyOKDAoGYDAZ5y7MifJQ2", name: "Premium", label: "Premium (£799/yr)" },
    { priceId: "price_1R4M5nKDAoGYDAZ5a0FTIRqs", name: "faannual", label: "FA Annual" },
  ];

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleGrantTrial = async () => {
    if (!trialPriceId) return;
    setActionLoading(true);
    try {
      const r = await admin_grant_trial_api(user._id, trialDays, trialPriceId);
      if (r.code === 200) {
        enqueueSnackbar(`${trialDays}-day trial on ${PLAN_OPTIONS.find(p => p.priceId === trialPriceId)?.label} granted`, { variant: "success" });
        setTrialDialogOpen(false); fetchStripeData();
      } else enqueueSnackbar(r.message || "Failed to grant trial", { variant: "error" });
    } catch { enqueueSnackbar("Failed to grant trial", { variant: "error" }); }
    finally { setActionLoading(false); }
  };

  const handleChangePlan = async () => {
    if (!selectedPriceId) return;
    setActionLoading(true);
    try {
      const r = await admin_change_plan_api(user._id, selectedPriceId);
      if (r.code === 200) {
        enqueueSnackbar("Plan changed successfully", { variant: "success" });
        setPlanDialogOpen(false); fetchStripeData();
      } else enqueueSnackbar(r.message || "Failed to change plan", { variant: "error" });
    } catch { enqueueSnackbar("Failed to change plan", { variant: "error" }); }
    finally { setActionLoading(false); }
  };

  const handleCancelSubscription = async () => {
    setActionLoading(true);
    try {
      const r = await admin_cancel_subscription_api(user._id, cancelImmediate);
      if (r.code === 200) {
        enqueueSnackbar(cancelImmediate ? "Subscription cancelled immediately" : "Subscription set to cancel at period end", { variant: "success" });
        setCancelDialogOpen(false); fetchStripeData();
      } else enqueueSnackbar(r.message || "Failed to cancel", { variant: "error" });
    } catch { enqueueSnackbar("Failed to cancel subscription", { variant: "error" }); }
    finally { setActionLoading(false); }
  };

  const handleAdjustCredits = async (isAdd) => {
    const amt = parseInt(creditAmount);
    if (!amt || amt <= 0) return;
    setCreditLoading(true);
    try {
      const r = await admin_adjust_tokens_api(user._id, isAdd ? amt : -amt, creditReason || (isAdd ? "Admin grant" : "Admin deduction"));
      if (r.code === 200) {
        enqueueSnackbar(`${isAdd ? "Added" : "Deducted"} ${amt} credits`, { variant: "success" });
        setAddCreditsOpen(false); setDeductCreditsOpen(false);
        setCreditAmount(""); setCreditReason("");
        fetchTokenData();
      } else enqueueSnackbar(r.message || "Failed to adjust credits", { variant: "error" });
    } catch { enqueueSnackbar("Failed to adjust credits", { variant: "error" }); }
    finally { setCreditLoading(false); }
  };

  const handleChargeCard = async () => {
    const amt = parseInt(creditAmount);
    if (!amt || amt <= 0) return;
    setCreditLoading(true);
    try {
      const r = await admin_charge_credits_api(user._id, amt);
      if (r.code === 200) {
        enqueueSnackbar(`Charged card for ${amt} credits`, { variant: "success" });
        setChargeCardOpen(false); setCreditAmount(""); fetchTokenData(); fetchStripeData();
      } else enqueueSnackbar(r.message || "Failed to charge card", { variant: "error" });
    } catch { enqueueSnackbar("Failed to charge card", { variant: "error" }); }
    finally { setCreditLoading(false); }
  };

  const handleResumePause = async () => {
    setPauseLoading(true);
    try {
      const r = await admin_resume_pause_api(user._id);
      if (r.code === 200) {
        enqueueSnackbar("Pause removed", { variant: "success" });
        setResumePauseOpen(false);
        // refresh user data if parent provides callback — otherwise just close
      } else enqueueSnackbar(r.message || "Failed to remove pause", { variant: "error" });
    } catch { enqueueSnackbar("Failed to remove pause", { variant: "error" }); }
    finally { setPauseLoading(false); }
  };

  const handleActivateInsurance = async () => {
    setInsuranceActionLoading(true);
    try {
      const r = await admin_activate_insurance_api(user._id);
      if (r.code === 200) { enqueueSnackbar("Pass Guarantee activated", { variant: "success" }); fetchInsuranceData(); }
      else enqueueSnackbar(r.message || "Failed to activate", { variant: "error" });
    } catch { enqueueSnackbar("Failed to activate", { variant: "error" }); }
    finally { setInsuranceActionLoading(false); }
  };

  const handleCancelInsurance = async () => {
    setInsuranceActionLoading(true);
    try {
      const r = await admin_cancel_insurance_api(user._id);
      if (r.code === 200) { enqueueSnackbar("Pass Guarantee cancelled", { variant: "success" }); setInsuranceCancelOpen(false); fetchInsuranceData(); }
      else enqueueSnackbar(r.message || "Failed to cancel", { variant: "error" });
    } catch { enqueueSnackbar("Failed to cancel", { variant: "error" }); }
    finally { setInsuranceActionLoading(false); }
  };

  if (!user) return null;

  const subscription  = stripeData?.subscription;
  const paymentMethod = stripeData?.paymentMethod;
  const invoices      = stripeData?.invoices || [];
  const totalSpend    = invoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0);
  const pause         = user.dataPause;
  const isPaused      = pause?.active === true;
  // Credits from token data (API) or fallback to what's on the user object
  const tokenBalance  = tokenData?.balance ?? tokenData?.purchasedBalance ?? null;
  const explCredits   = user.explanationCredits?.balance ?? null;

  return (
    <CenteredDetailModal open={open} onClose={onClose} maxWidth={620}>

      {/* Header */}
      <Box sx={{ p: 3, borderBottom: "1px solid rgba(26,26,26,0.09)", display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "#ffffff" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar src={user.profile_image ? baseUrl + user.profile_image : undefined}
            sx={{ width: 52, height: 52, backgroundColor: "#c01e2e", fontSize: "18px" }}>
            {user.first_name?.[0]}{user.last_name?.[0]}
          </Avatar>
          <Box>
            <Typography sx={{ color: "#1a1a1a", fontSize: "17px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
              {user.first_name} {user.last_name}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75, mt: 0.5, flexWrap: "wrap" }}>
              <Chip label={user.status ? "Active" : "Inactive"} size="small"
                sx={{ borderRadius: "6px", fontSize: "11px", fontWeight: 500,
                  backgroundColor: user.status ? "rgba(22,163,74,0.1)" : "rgba(192,30,46,0.1)",
                  color: user.status ? "#16a34a" : "#c01e2e" }} />
              {isPaused && (
                <Chip label="Paused" size="small" icon={<PauseCircleIcon sx={{ fontSize: "12px !important" }} />}
                  sx={{ borderRadius: "6px", fontSize: "11px", fontWeight: 500, backgroundColor: "rgba(217,119,6,0.1)", color: "#d97706" }} />
              )}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#aaa", "&:hover": { color: "#1a1a1a" } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3, overflowY: "auto", flex: 1, backgroundColor: "#FAF7F2" }}>

        {/* ── Credits ─────────────────────────────────────────────────────── */}
        <Section title="Credits">
          {tokenLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} sx={{ color: "#c01e2e" }} />
            </Box>
          ) : (
            <>
              {/* Balance + spend row */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, py: 1.5 }}>
                <Box sx={{ textAlign: "center", p: 1.5, backgroundColor: "#ffffff", borderRadius: "10px", border: "1px solid rgba(26,26,26,0.09)" }}>
                  <Typography sx={{ color: "#1a1a1a", fontSize: "26px", fontWeight: 700, fontFamily: "'Rubik', sans-serif", lineHeight: 1.1 }}>
                    {tokenBalance !== null ? tokenBalance.toLocaleString() : "—"}
                  </Typography>
                  <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif", mt: 0.25 }}>
                    Credit Balance
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center", p: 1.5, backgroundColor: "#ffffff", borderRadius: "10px", border: "1px solid rgba(26,26,26,0.09)" }}>
                  <Typography sx={{ color: "#1a1a1a", fontSize: "26px", fontWeight: 700, fontFamily: "'Rubik', sans-serif", lineHeight: 1.1 }}>
                    {stripeLoading ? "…" : `£${(totalSpend / 100).toFixed(2)}`}
                  </Typography>
                  <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif", mt: 0.25 }}>
                    Total Spend
                  </Typography>
                </Box>
              </Box>

              {/* Explanation credits (separate pool) */}
              {explCredits !== null && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, pb: 1 }}>
                  <Typography sx={{ color: "#6b6b6b", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>
                    Explanation Credits
                  </Typography>
                  <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
                    {explCredits.toLocaleString()}
                  </Typography>
                </Box>
              )}

              {/* Actions */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, pb: 1.5, pt: 0.5 }}>
                {[
                  { label: "Add", icon: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />, color: "#16a34a", bg: "rgba(22,163,74,0.08)", border: "rgba(22,163,74,0.2)", onClick: () => { setCreditAmount(""); setCreditReason(""); setAddCreditsOpen(true); } },
                  { label: "Deduct", icon: <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />, color: "#d97706", bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.2)", onClick: () => { setCreditAmount(""); setCreditReason(""); setDeductCreditsOpen(true); } },
                  { label: "Charge Card", icon: <CreditCardIcon sx={{ fontSize: 16 }} />, color: "#2563eb", bg: "rgba(37,99,235,0.08)", border: "rgba(37,99,235,0.2)", onClick: () => { setCreditAmount(""); setChargeCardOpen(true); } },
                ].map(({ label, icon, color, bg, border, onClick }) => (
                  <Box key={label} onClick={onClick}
                    sx={{ cursor: "pointer", p: 1.25, borderRadius: "9px", border: `1px solid ${border}`, backgroundColor: bg,
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5,
                      transition: "all 0.15s ease",
                      "&:hover": { filter: "brightness(0.96)", transform: "translateY(-1px)" } }}>
                    <Box sx={{ color }}>{icon}</Box>
                    <Typography sx={{ color, fontSize: "11px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Section>


        {/* ── Contact Information ──────────────────────────────────────────── */}
        <Section title="Contact Information">
          <InfoRow icon={<EmailIcon fontSize="small" />} label="Email" value={user.email} />
          <InfoRow icon={<PhoneIcon fontSize="small" />} label="Phone" value={user.phone_number} />
          {user.signupLocation?.countryCode && (
            <InfoRow icon={<LocationOnIcon fontSize="small" />} label="Signup Location" value={user.signupLocation.countryCode} />
          )}
        </Section>

        {/* ── Account Details ──────────────────────────────────────────────── */}
        <Section title="Account Details">
          <InfoRow icon={<CalendarTodayIcon fontSize="small" />} label="Account Created" value={user.createdAt ? moment(user.createdAt).format("DD MMM YYYY, HH:mm") : null} />
          <InfoRow icon={<AccessTimeIcon fontSize="small" />} label="Last Login" value={user.lastLogin ? moment(user.lastLogin).format("DD MMM YYYY, HH:mm") : "Never"} />
          <InfoRow
            icon={user.auth_provider === "google" ? <GoogleIcon fontSize="small" /> : <LockIcon fontSize="small" />}
            label="Auth Provider"
            value={user.auth_provider === "google" ? "Google" : "Email/Password"}
            chip chipColor={user.auth_provider === "google" ? "info" : "default"}
          />
        </Section>

        {/* ── Progress ─────────────────────────────────────────────────────── */}
        <Section title="Progress">
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.25, py: 1.5 }}>
            {/* Questions */}
            <Box sx={{ textAlign: "center", p: 1.5, backgroundColor: "#ffffff", borderRadius: "10px", border: "1px solid rgba(26,26,26,0.09)" }}>
              <Typography sx={{ color: "#1a1a1a", fontSize: "22px", fontWeight: 700, fontFamily: "'Rubik', sans-serif" }}>
                {(user.totalQuestions || 0).toLocaleString()}
              </Typography>
              <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>Questions</Typography>
            </Box>

            {/* Accuracy */}
            <Box sx={{ textAlign: "center", p: 1.5, backgroundColor: "#ffffff", borderRadius: "10px", border: "1px solid rgba(26,26,26,0.09)" }}>
              <Typography sx={{
                color: (user.accuracy || 0) >= 70 ? "#16a34a" : (user.accuracy || 0) >= 50 ? "#d97706" : "#c01e2e",
                fontSize: "22px", fontWeight: 700, fontFamily: "'Rubik', sans-serif" }}>
                {user.accuracy ? `${Math.round(user.accuracy)}%` : "—"}
              </Typography>
              <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>Accuracy</Typography>
            </Box>

            {/* Lessons */}
            <Box sx={{ textAlign: "center", p: 1.5, backgroundColor: "#ffffff", borderRadius: "10px", border: "1px solid rgba(26,26,26,0.09)" }}>
              {lessonLoading ? (
                <CircularProgress size={18} sx={{ color: "#c01e2e", mt: 0.5 }} />
              ) : (
                <Typography sx={{ color: "#1a1a1a", fontSize: "22px", fontWeight: 700, fontFamily: "'Rubik', sans-serif" }}>
                  {lessonData ? lessonData.total.toFixed(1) : "—"}
                </Typography>
              )}
              <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>Lessons</Typography>
            </Box>
          </Box>

          {/* In-progress lesson detail */}
          {lessonData?.inProgress?.length > 0 && (
            <Box sx={{ pb: 1 }}>
              {lessonData.inProgress.map((s, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.75,
                  borderTop: "1px solid rgba(26,26,26,0.06)" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "#1a1a1a", fontSize: "12px", fontWeight: 500, fontFamily: "'Rubik', sans-serif",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {s.topic}
                    </Typography>
                    <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>
                      {s.subject} · {s.slidesSeen}/{s.totalSlides} slides
                    </Typography>
                  </Box>
                  {/* Mini progress bar */}
                  <Box sx={{ ml: 1.5, flexShrink: 0, width: 60 }}>
                    <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "rgba(26,26,26,0.1)", overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${Math.round(s.progress * 100)}%`,
                        backgroundColor: "#c01e2e", borderRadius: 2, transition: "width 0.3s ease" }} />
                    </Box>
                    <Typography sx={{ color: "#aaa", fontSize: "10px", fontFamily: "'Rubik', sans-serif", textAlign: "right", mt: 0.25 }}>
                      {Math.round(s.progress * 100)}%
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Section>

        {/* ── Manage Subscription ──────────────────────────────────────────── */}
        {user.stripeCustomerId && (
          <Section title="Manage Subscription">
            {stripeLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} sx={{ color: "#c01e2e" }} />
              </Box>
            ) : stripeError ? (
              <Typography sx={{ color: "#aaa", fontSize: "12px", py: 1.5, fontFamily: "'Rubik', sans-serif" }}>{stripeError}</Typography>
            ) : (
              <>
                {user.paymentPlan && (
                  <InfoRow icon={<PaymentIcon fontSize="small" />} label="Current Plan" value={user.paymentPlan} />
                )}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, py: 1.25 }}>
                  {[
                    { label: "Grant Trial", icon: <CardGiftcardIcon sx={{ fontSize: 18 }} />, color: "#16a34a", bg: "rgba(22,163,74,0.08)", border: "rgba(22,163,74,0.2)", onClick: () => { setTrialDays(7); setTrialPriceId(""); setTrialDialogOpen(true); } },
                    { label: "Change Plan", icon: <SwapHorizIcon sx={{ fontSize: 18 }} />,    color: "#2563eb", bg: "rgba(37,99,235,0.08)",  border: "rgba(37,99,235,0.2)",  onClick: () => { setSelectedPriceId(""); setPlanDialogOpen(true); } },
                  ].map(({ label, icon, color, bg, border, onClick }) => (
                    <Box key={label} onClick={onClick}
                      sx={{ cursor: "pointer", p: 1.75, borderRadius: "10px", border: `1px solid ${border}`, backgroundColor: bg,
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, transition: "all 0.15s ease",
                        "&:hover": { filter: "brightness(0.96)", transform: "translateY(-1px)" } }}>
                      <Box sx={{ color }}>{icon}</Box>
                      <Typography sx={{ color, fontSize: "12px", fontWeight: 600, fontFamily: "'Rubik', sans-serif", textAlign: "center" }}>{label}</Typography>
                    </Box>
                  ))}
                  {subscription && (
                    <Box onClick={() => { setCancelImmediate(false); setCancelDialogOpen(true); }}
                      sx={{ cursor: "pointer", p: 1.75, borderRadius: "10px", border: "1px solid rgba(192,30,46,0.2)", backgroundColor: "rgba(192,30,46,0.04)",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, gridColumn: "span 2", transition: "all 0.15s ease",
                        "&:hover": { backgroundColor: "rgba(192,30,46,0.09)", border: "1px solid rgba(192,30,46,0.4)", transform: "translateY(-1px)" } }}>
                      <CancelIcon sx={{ color: "#c01e2e", fontSize: 18 }} />
                      <Typography sx={{ color: "#c01e2e", fontSize: "12px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Cancel Subscription</Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Section>
        )}

        {/* ── Pass Guarantee ───────────────────────────────────────────────── */}
        <Section title="Pass Guarantee">
          {insuranceLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} sx={{ color: "#c01e2e" }} /></Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, borderRadius: "9px", mt: 1, mb: 1.5,
                backgroundColor: insuranceData?.hasInsurance && insuranceData.insurance?.status === "active" ? "rgba(22,163,74,0.08)" : "rgba(26,26,26,0.04)",
                border: insuranceData?.hasInsurance && insuranceData.insurance?.status === "active" ? "1px solid rgba(22,163,74,0.2)" : "1px solid rgba(26,26,26,0.09)" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <ShieldIcon sx={{ fontSize: 18, color: insuranceData?.hasInsurance && insuranceData.insurance?.status === "active" ? "#16a34a" : "#aaa" }} />
                  <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
                    {insuranceData?.hasInsurance && insuranceData.insurance?.status === "active" ? "Active"
                      : insuranceData?.hasInsurance && insuranceData.insurance?.status === "pending" ? "Pending Payment" : "Not Active"}
                  </Typography>
                </Box>
                <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>£8.99/mo</Typography>
              </Box>
              <Box sx={{ pb: 1 }}>
                {(!insuranceData?.hasInsurance || insuranceData.insurance?.status !== "active") ? (
                  <Box onClick={() => !insuranceActionLoading && handleActivateInsurance()}
                    sx={{ cursor: "pointer", p: 1.5, borderRadius: "9px", border: "1px solid rgba(22,163,74,0.2)", backgroundColor: "rgba(22,163,74,0.06)",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 1, opacity: insuranceActionLoading ? 0.6 : 1,
                      "&:hover": { backgroundColor: "rgba(22,163,74,0.11)" } }}>
                    {insuranceActionLoading ? <CircularProgress size={16} sx={{ color: "#16a34a" }} /> : <ShieldIcon sx={{ color: "#16a34a", fontSize: 16 }} />}
                    <Typography sx={{ color: "#16a34a", fontSize: "12px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Activate Pass Guarantee</Typography>
                  </Box>
                ) : (
                  <Box onClick={() => !insuranceActionLoading && setInsuranceCancelOpen(true)}
                    sx={{ cursor: "pointer", p: 1.5, borderRadius: "9px", border: "1px solid rgba(192,30,46,0.2)", backgroundColor: "rgba(192,30,46,0.04)",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
                      "&:hover": { backgroundColor: "rgba(192,30,46,0.09)" } }}>
                    <CancelIcon sx={{ color: "#c01e2e", fontSize: 16 }} />
                    <Typography sx={{ color: "#c01e2e", fontSize: "12px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Cancel Pass Guarantee</Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Section>

        {/* ── Payment Method ───────────────────────────────────────────────── */}
        {paymentMethod && (
          <Section title="Payment Method" collapsible defaultOpen={false}>
            <InfoRow icon={<CreditCardIcon fontSize="small" />} label="Card" value={`${paymentMethod.card?.brand?.toUpperCase()} •••• ${paymentMethod.card?.last4}`} />
            <InfoRow icon={<CalendarTodayIcon fontSize="small" />} label="Expires" value={`${paymentMethod.card?.exp_month}/${paymentMethod.card?.exp_year}`} />
          </Section>
        )}

        {/* ── Recent Invoices ──────────────────────────────────────────────── */}
        {invoices.length > 0 && (
          <Section title="Recent Invoices" collapsible defaultOpen={false}>
            {invoices.slice(0, 8).map((inv, idx) => (
              <Box key={inv.id || idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.25,
                borderBottom: idx < Math.min(invoices.length, 8) - 1 ? "1px solid rgba(26,26,26,0.06)" : "none" }}>
                <Box>
                  <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{formatStripeDate(inv.created)}</Typography>
                  <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>{inv.number}</Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>
                    {formatCurrency(inv.amount_paid, inv.currency)}
                  </Typography>
                  <Chip label={inv.status} size="small" color={inv.status === "paid" ? "success" : "default"}
                    sx={{ fontSize: "10px", height: "18px", borderRadius: "4px" }} />
                </Box>
              </Box>
            ))}
          </Section>
        )}

        {/* ── Referral Program ─────────────────────────────────────────────── */}
        <Section title="Referral Program" collapsible defaultOpen={false}>
          <InfoRow icon={<CardGiftcardIcon fontSize="small" />} label="Referral Code" value={user.referralCode || "None"} />
          <InfoRow icon={<PersonIcon fontSize="small" />} label="Referral Count" value={user.referralCount || 0} />
          <InfoRow icon={<CardGiftcardIcon fontSize="small" />} label="Redeemed Code"
            value={user.hasRedeemedReferralCode ? (user.redeemedReferralCode || "Yes") : "No"}
            chip chipColor={user.hasRedeemedReferralCode ? "success" : "default"} />
        </Section>

        {/* ── Preferences ──────────────────────────────────────────────────── */}
        <Section title="Preferences" collapsible defaultOpen={false}>
          <InfoRow icon={<EmailIcon fontSize="small" />} label="Email Subscribed" value={user.emailSubbed ? "Yes" : "No"} chip chipColor={user.emailSubbed ? "success" : "default"} />
        </Section>

        {/* ── Internal IDs ─────────────────────────────────────────────────── */}
        <Section title="Internal IDs" collapsible defaultOpen={false}>
          <InfoRow icon={<LockIcon fontSize="small" />} label="User ID" value={user._id} mono />
          {user.stripeCustomerId && <InfoRow icon={<ReceiptIcon fontSize="small" />} label="Stripe Customer" value={user.stripeCustomerId} mono />}
          {user.google_id && <InfoRow icon={<GoogleIcon fontSize="small" />} label="Google ID" value={user.google_id} mono />}
        </Section>

      </Box>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/*  DIALOGS                                                            */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      {/* ── Add Credits ──────────────────────────────────────────────────── */}
      <Dialog open={addCreditsOpen} onClose={() => !creditLoading && setAddCreditsOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#ffffff", borderRadius: "14px", minWidth: 380, border: "1px solid rgba(22,163,74,0.2)" } }}>
        <Box sx={{ p: 3, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", backgroundColor: "rgba(22,163,74,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AddCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Add Credits</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{user.first_name} {user.last_name}</Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 1 }}>
          <TextField label="Amount" type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)}
            fullWidth size="small" inputProps={{ min: 1 }} sx={{ ...dialogInputSx, mb: 2 }} />
          <TextField label="Reason (optional)" value={creditReason} onChange={(e) => setCreditReason(e.target.value)}
            fullWidth size="small" placeholder="e.g. Goodwill gesture, refund…" sx={dialogInputSx} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setAddCreditsOpen(false)} disabled={creditLoading}
            sx={{ color: "#6b6b6b", borderRadius: "9px", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Cancel</Button>
          <Button onClick={() => handleAdjustCredits(true)} variant="contained" disabled={creditLoading || !creditAmount}
            sx={{ borderRadius: "9px", textTransform: "none", px: 3, fontFamily: "'Rubik', sans-serif", backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}>
            {creditLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : `Add ${creditAmount || 0} Credits`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Deduct Credits ───────────────────────────────────────────────── */}
      <Dialog open={deductCreditsOpen} onClose={() => !creditLoading && setDeductCreditsOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#ffffff", borderRadius: "14px", minWidth: 380, border: "1px solid rgba(217,119,6,0.2)" } }}>
        <Box sx={{ p: 3, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", backgroundColor: "rgba(217,119,6,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <RemoveCircleOutlineIcon sx={{ color: "#d97706", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Deduct Credits</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{user.first_name} {user.last_name}</Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 1 }}>
          <TextField label="Amount" type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)}
            fullWidth size="small" inputProps={{ min: 1 }} sx={{ ...dialogInputSx, mb: 2 }} />
          <TextField label="Reason" value={creditReason} onChange={(e) => setCreditReason(e.target.value)}
            fullWidth size="small" required sx={dialogInputSx} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setDeductCreditsOpen(false)} disabled={creditLoading}
            sx={{ color: "#6b6b6b", borderRadius: "9px", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Cancel</Button>
          <Button onClick={() => handleAdjustCredits(false)} variant="contained" disabled={creditLoading || !creditAmount || !creditReason}
            sx={{ borderRadius: "9px", textTransform: "none", px: 3, fontFamily: "'Rubik', sans-serif", backgroundColor: "#d97706", "&:hover": { backgroundColor: "#b45309" } }}>
            {creditLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : `Deduct ${creditAmount || 0} Credits`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Charge Card for Credits ──────────────────────────────────────── */}
      <Dialog open={chargeCardOpen} onClose={() => !creditLoading && setChargeCardOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#ffffff", borderRadius: "14px", minWidth: 380, border: "1px solid rgba(37,99,235,0.2)" } }}>
        <Box sx={{ p: 3, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", backgroundColor: "rgba(37,99,235,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CreditCardIcon sx={{ color: "#2563eb", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Charge Card for Credits</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{user.first_name} {user.last_name}</Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 1 }}>
          {paymentMethod ? (
            <Box sx={{ mb: 2, p: 1.25, borderRadius: "8px", backgroundColor: "#F7F4EF", border: "1px solid rgba(26,26,26,0.09)", display: "flex", alignItems: "center", gap: 1 }}>
              <CreditCardIcon sx={{ color: "#aaa", fontSize: 16 }} />
              <Typography sx={{ color: "#6b6b6b", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>
                {paymentMethod.card?.brand?.toUpperCase()} •••• {paymentMethod.card?.last4}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mb: 2, p: 1.25, borderRadius: "8px", backgroundColor: "rgba(192,30,46,0.06)", border: "1px solid rgba(192,30,46,0.15)" }}>
              <Typography sx={{ color: "#c01e2e", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>No saved payment method on file</Typography>
            </Box>
          )}
          <TextField label="Credits to add" type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)}
            fullWidth size="small" inputProps={{ min: 50, step: 50 }}
            helperText="Min 50 — charged to user's default card" sx={dialogInputSx} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setChargeCardOpen(false)} disabled={creditLoading}
            sx={{ color: "#6b6b6b", borderRadius: "9px", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Cancel</Button>
          <Button onClick={handleChargeCard} variant="contained" disabled={creditLoading || !creditAmount || !paymentMethod}
            sx={{ borderRadius: "9px", textTransform: "none", px: 3, fontFamily: "'Rubik', sans-serif", backgroundColor: "#2563eb", "&:hover": { backgroundColor: "#1d4ed8" } }}>
            {creditLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Charge Card"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Remove Pause ─────────────────────────────────────────────────── */}
      <Dialog open={resumePauseOpen} onClose={() => !pauseLoading && setResumePauseOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#ffffff", borderRadius: "14px", minWidth: 360, border: "1px solid rgba(22,163,74,0.2)" } }}>
        <Box sx={{ p: 3, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", backgroundColor: "rgba(22,163,74,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <PlayCircleIcon sx={{ color: "#16a34a", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Remove Pause</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{user.first_name} {user.last_name}</Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 0 }}>
          <Typography sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: "'Rubik', sans-serif", lineHeight: 1.5 }}>
            This will remove the user's active data pause. Their account data is preserved and they'll remain on the Basic plan — they can upgrade separately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setResumePauseOpen(false)} disabled={pauseLoading}
            sx={{ color: "#6b6b6b", borderRadius: "9px", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Cancel</Button>
          <Button onClick={handleResumePause} variant="contained" disabled={pauseLoading}
            sx={{ borderRadius: "9px", textTransform: "none", px: 3, fontFamily: "'Rubik', sans-serif", backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}>
            {pauseLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Remove Pause"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Grant Trial ──────────────────────────────────────────────────── */}
      <Dialog open={trialDialogOpen} onClose={() => !actionLoading && setTrialDialogOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#ffffff", borderRadius: "14px", minWidth: 420, border: "1px solid rgba(22,163,74,0.25)" } }}>
        <Box sx={{ p: 3, pb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", backgroundColor: "rgba(22,163,74,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CardGiftcardIcon sx={{ color: "#16a34a", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Grant Trial</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{user.first_name} {user.last_name}</Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 2 }}>
          <Typography sx={{ color: "#aaa", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5, fontFamily: "'Rubik', sans-serif" }}>
            Select Plan
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 2 }}>
            {PLAN_OPTIONS.map((plan) => (
              <Box key={plan.priceId} onClick={() => setTrialPriceId(plan.priceId)}
                sx={{ p: 1.25, borderRadius: "9px", cursor: "pointer",
                  border: trialPriceId === plan.priceId ? "1px solid rgba(22,163,74,0.5)" : "1px solid rgba(26,26,26,0.12)",
                  backgroundColor: trialPriceId === plan.priceId ? "rgba(22,163,74,0.08)" : "#F7F4EF",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  "&:hover": { backgroundColor: trialPriceId === plan.priceId ? "rgba(22,163,74,0.08)" : "#F2EDE4" } }}>
                <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>{plan.label}</Typography>
                {trialPriceId === plan.priceId && <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 18 }} />}
              </Box>
            ))}
          </Box>
          <Typography sx={{ color: "#aaa", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.25, fontFamily: "'Rubik', sans-serif" }}>Duration</Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            {[3, 7, 14, 30].map((d) => (
              <Chip key={d} label={`${d}d`} size="small" onClick={() => setTrialDays(d)}
                sx={{ borderRadius: "7px", fontWeight: 600, fontSize: "12px", cursor: "pointer", fontFamily: "'Rubik', sans-serif",
                  backgroundColor: trialDays === d ? "rgba(22,163,74,0.15)" : "rgba(26,26,26,0.06)",
                  color: trialDays === d ? "#16a34a" : "#6b6b6b",
                  border: trialDays === d ? "1px solid rgba(22,163,74,0.4)" : "1px solid transparent" }} />
            ))}
          </Box>
          <TextField type="number" label="Custom days" value={trialDays} onChange={(e) => setTrialDays(parseInt(e.target.value) || 1)}
            fullWidth size="small" inputProps={{ min: 1, max: 365 }} sx={dialogInputSx} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setTrialDialogOpen(false)} disabled={actionLoading}
            sx={{ color: "#6b6b6b", borderRadius: "9px", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Cancel</Button>
          <Button onClick={handleGrantTrial} variant="contained" disabled={actionLoading || !trialPriceId}
            sx={{ borderRadius: "9px", textTransform: "none", px: 3, fontFamily: "'Rubik', sans-serif", backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}>
            {actionLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Grant Trial"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Change Plan ───────────────────────────────────────────────────── */}
      <Dialog open={planDialogOpen} onClose={() => !actionLoading && setPlanDialogOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#ffffff", borderRadius: "14px", minWidth: 420, border: "1px solid rgba(37,99,235,0.2)" } }}>
        <Box sx={{ p: 3, pb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", backgroundColor: "rgba(37,99,235,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SwapHorizIcon sx={{ color: "#2563eb", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Change Plan</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>
                Currently on <strong style={{ color: "#1a1a1a" }}>{user.paymentPlan || "None"}</strong>
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {PLAN_OPTIONS.map((plan) => {
              const currentPriceId = stripeData?.subscription?.items?.data?.[0]?.price?.id;
              const isCurrent = currentPriceId === plan.priceId;
              return (
                <Box key={plan.priceId} onClick={() => !isCurrent && setSelectedPriceId(plan.priceId)}
                  sx={{ p: 1.25, borderRadius: "9px", cursor: isCurrent ? "default" : "pointer",
                    border: selectedPriceId === plan.priceId ? "1px solid rgba(37,99,235,0.5)" : "1px solid rgba(26,26,26,0.12)",
                    backgroundColor: selectedPriceId === plan.priceId ? "rgba(37,99,235,0.08)" : "#F7F4EF",
                    opacity: isCurrent ? 0.45 : 1,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    "&:hover": { backgroundColor: isCurrent ? "#F7F4EF" : selectedPriceId === plan.priceId ? "rgba(37,99,235,0.08)" : "#F2EDE4" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>{plan.label}</Typography>
                    {isCurrent && <Chip label="Current" size="small" sx={{ height: 20, fontSize: "10px", backgroundColor: "rgba(26,26,26,0.08)", color: "#6b6b6b" }} />}
                  </Box>
                  {selectedPriceId === plan.priceId && <CheckCircleOutlineIcon sx={{ color: "#2563eb", fontSize: 18 }} />}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setPlanDialogOpen(false)} disabled={actionLoading}
            sx={{ color: "#6b6b6b", borderRadius: "9px", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Cancel</Button>
          <Button onClick={handleChangePlan} variant="contained" disabled={actionLoading || !selectedPriceId}
            sx={{ borderRadius: "9px", textTransform: "none", px: 3, fontFamily: "'Rubik', sans-serif", backgroundColor: "#2563eb", "&:hover": { backgroundColor: "#1d4ed8" } }}>
            {actionLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Change Plan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Cancel Subscription ──────────────────────────────────────────── */}
      <Dialog open={cancelDialogOpen} onClose={() => !actionLoading && setCancelDialogOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#ffffff", borderRadius: "14px", minWidth: 420, border: "1px solid rgba(192,30,46,0.2)" } }}>
        <Box sx={{ p: 3, pb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", backgroundColor: "rgba(192,30,46,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CancelIcon sx={{ color: "#c01e2e", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Cancel Subscription</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{user.first_name} {user.last_name}</Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {[
              { key: false, label: "At period end", desc: "User keeps access until billing period ends", accent: "#d97706" },
              { key: true,  label: "Immediately",   desc: "Access revoked now, reverts to Basic",        accent: "#c01e2e" },
            ].map(({ key, label, desc, accent }) => (
              <Box key={String(key)} onClick={() => setCancelImmediate(key)}
                sx={{ p: 1.75, borderRadius: "9px", cursor: "pointer",
                  border: cancelImmediate === key ? `1px solid ${accent}55` : "1px solid rgba(26,26,26,0.12)",
                  backgroundColor: cancelImmediate === key ? `${accent}11` : "#F7F4EF",
                  "&:hover": { backgroundColor: cancelImmediate === key ? `${accent}11` : "#F2EDE4" } }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.4 }}>
                  <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>{label}</Typography>
                  {cancelImmediate === key && <CheckCircleOutlineIcon sx={{ color: accent, fontSize: 18 }} />}
                </Box>
                <Typography sx={{ color: "#6b6b6b", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{desc}</Typography>
              </Box>
            ))}
          </Box>
          {cancelImmediate && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: "8px", backgroundColor: "rgba(192,30,46,0.07)", border: "1px solid rgba(192,30,46,0.2)", display: "flex", gap: 1 }}>
              <WarningAmberIcon sx={{ color: "#c01e2e", fontSize: 18, mt: 0.1, flexShrink: 0 }} />
              <Typography sx={{ color: "#c01e2e", fontSize: "12px", lineHeight: 1.5, fontFamily: "'Rubik', sans-serif" }}>
                Irreversible. The user will lose access immediately and revert to Basic.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={actionLoading}
            sx={{ color: "#6b6b6b", borderRadius: "9px", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Back</Button>
          <Button onClick={handleCancelSubscription} variant="contained" disabled={actionLoading}
            sx={{ borderRadius: "9px", textTransform: "none", px: 3, fontFamily: "'Rubik', sans-serif",
              backgroundColor: cancelImmediate ? "#c01e2e" : "#d97706",
              "&:hover": { backgroundColor: cancelImmediate ? "#a81828" : "#b45309" } }}>
            {actionLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : cancelImmediate ? "Cancel Now" : "Cancel at Period End"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Cancel Insurance ─────────────────────────────────────────────── */}
      <Dialog open={insuranceCancelOpen} onClose={() => !insuranceActionLoading && setInsuranceCancelOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#ffffff", borderRadius: "14px", minWidth: 380, border: "1px solid rgba(192,30,46,0.2)" } }}>
        <Box sx={{ p: 3, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", backgroundColor: "rgba(192,30,46,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShieldIcon sx={{ color: "#c01e2e", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>Cancel Pass Guarantee</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{user.first_name} {user.last_name}</Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 0 }}>
          <Box sx={{ p: 1.5, borderRadius: "8px", backgroundColor: "rgba(192,30,46,0.07)", border: "1px solid rgba(192,30,46,0.2)", display: "flex", gap: 1 }}>
            <WarningAmberIcon sx={{ color: "#c01e2e", fontSize: 18, mt: 0.1, flexShrink: 0 }} />
            <Typography sx={{ color: "#c01e2e", fontSize: "12px", lineHeight: 1.5, fontFamily: "'Rubik', sans-serif" }}>
              This will cancel the user's Pass Guarantee and any associated Stripe insurance subscription.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setInsuranceCancelOpen(false)} disabled={insuranceActionLoading}
            sx={{ color: "#6b6b6b", borderRadius: "9px", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Back</Button>
          <Button onClick={handleCancelInsurance} variant="contained" disabled={insuranceActionLoading}
            sx={{ borderRadius: "9px", textTransform: "none", px: 3, fontFamily: "'Rubik', sans-serif", backgroundColor: "#c01e2e", "&:hover": { backgroundColor: "#a81828" } }}>
            {insuranceActionLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Cancel Insurance"}
          </Button>
        </DialogActions>
      </Dialog>

    </CenteredDetailModal>
  );
}
